import { useEffect, useState } from 'react';
import { api } from '../api.js';
import {
  tokens, couleursStatut, couleursGravite, couleursImpact, couleursUrgence, couleursType,
  STATUTS_ACTIFS, STATUTS_CLOS,
} from '../tokens.js';
import { Badge, formaterDateRelative } from '../helpers.jsx';
import { Filter, Search, AlertCircle, Inbox } from 'lucide-react';

const STATUTS_FILTRES = [
  { val: '',                     label: 'Tous statuts' },
  { val: 'nouveau',              label: 'Nouveau' },
  { val: 'clarification_ia',     label: 'Clarif IA' },
  { val: 'clarification_admin',  label: 'Clarif admin' },
  { val: 'pris_en_compte',       label: 'Pris en compte' },
  { val: 'en_cours',             label: 'En cours' },
  { val: 'resolu',               label: 'Résolu' },
  { val: 'refuse',               label: 'Refusé' },
  { val: 'doublon',              label: 'Doublon' },
];

export function AdminDashboard({ onOuvrirTicket }) {
  const [tickets, setTickets] = useState([]);
  const [services, setServices] = useState([]);
  const [chargement, setChargement] = useState(true);
  const [erreur, setErreur] = useState('');
  const [mode, setMode] = useState('actifs'); // actifs | clos | tous
  const [filtres, setFiltres] = useState({
    statut: '', gravite: '', urgence: '', service_id: '', q: '',
  });

  useEffect(() => {
    api.get('/admin/services')
      .then(d => setServices(d.services || []))
      .catch(() => {});
  }, []);

  useEffect(() => {
    setChargement(true);
    const params = new URLSearchParams();
    // Si statut specifique pas choisi, on filtre selon le mode (actifs/clos/tous)
    const statutEnvoye = filtres.statut
      || (mode === 'actifs' ? STATUTS_ACTIFS.join(',')
         : mode === 'clos'   ? STATUTS_CLOS.join(',')
         : '');
    if (statutEnvoye) params.append('statut', statutEnvoye);
    for (const [k, v] of Object.entries(filtres)) {
      if (v && k !== 'statut') params.append(k, v);
    }
    api.get('/admin/tickets' + (params.toString() ? '?' + params.toString() : ''))
      .then(d => setTickets(d.tickets || []))
      .catch(e => setErreur(e.message))
      .finally(() => setChargement(false));
  }, [filtres, mode]);

  function changerMode(m) {
    setMode(m);
    // Reset le statut specifique pour eviter conflit (ex: actifs + 'resolu')
    setFiltres(f => ({ ...f, statut: '' }));
  }

  return (
    <div>
      <div style={{ marginBottom: 18 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 600, color: tokens.ink }}>
          Tous les tickets
        </h2>
        <p style={{ margin: '4px 0 0', fontSize: 13, color: tokens.inkSoft }}>
          Vue admin · les tickets urgents sont en haut
        </p>
      </div>

      {/* Onglets cycle de vie */}
      <div style={{ display:'flex', gap: 4, marginBottom: 14 }}>
        {[
          { val: 'actifs', label: 'Actifs' },
          { val: 'clos',   label: 'Clos & refusés' },
          { val: 'tous',   label: 'Tous' },
        ].map(o => (
          <button
            key={o.val}
            onClick={() => changerMode(o.val)}
            style={{
              padding: '7px 14px',
              border: 'none', cursor:'pointer',
              borderRadius: 6,
              background: mode === o.val ? tokens.accent : tokens.surface,
              color:      mode === o.val ? 'white' : tokens.inkSoft,
              fontSize: 13, fontWeight: 500,
              borderBottom: `1px solid ${tokens.border}`,
            }}
          >
            {o.label}
          </button>
        ))}
      </div>

      {/* Filtres */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
        gap: 10,
        background: tokens.surface,
        border: `1px solid ${tokens.border}`,
        borderRadius: 10,
        padding: 14,
        marginBottom: 14,
      }}>
        <ChampFiltre label="Statut">
          <select
            value={filtres.statut}
            onChange={(e) => setFiltres(f => ({ ...f, statut: e.target.value }))}
            style={selectStyle}
          >
            {STATUTS_FILTRES.map(s => <option key={s.val} value={s.val}>{s.label}</option>)}
          </select>
        </ChampFiltre>
        <ChampFiltre label="Gravité IA">
          <select
            value={filtres.gravite}
            onChange={(e) => setFiltres(f => ({ ...f, gravite: e.target.value }))}
            style={selectStyle}
          >
            <option value="">Toutes</option>
            <option value="bloquant">Bloquant</option>
            <option value="majeur">Majeur</option>
            <option value="mineur">Mineur</option>
            <option value="cosmetique">Cosmétique</option>
          </select>
        </ChampFiltre>
        <ChampFiltre label="Urgence">
          <select
            value={filtres.urgence}
            onChange={(e) => setFiltres(f => ({ ...f, urgence: e.target.value }))}
            style={selectStyle}
          >
            <option value="">Toutes</option>
            <option value="urgent">Urgent</option>
            <option value="peut_attendre">Peut attendre</option>
          </select>
        </ChampFiltre>
        <ChampFiltre label="Service">
          <select
            value={filtres.service_id}
            onChange={(e) => setFiltres(f => ({ ...f, service_id: e.target.value }))}
            style={selectStyle}
          >
            <option value="">Tous</option>
            {services.map(s => (
              <option key={s.id} value={s.id}>{s.nom}</option>
            ))}
          </select>
        </ChampFiltre>
        <ChampFiltre label="Recherche" style={{ gridColumn: 'span 2' }}>
          <div style={{ position:'relative' }}>
            <Search
              size={14}
              color={tokens.inkMuted}
              style={{ position:'absolute', left: 10, top: 11, pointerEvents:'none' }}
            />
            <input
              type="text"
              value={filtres.q}
              onChange={(e) => setFiltres(f => ({ ...f, q: e.target.value }))}
              placeholder="Token, résumé IA, description…"
              style={{ ...selectStyle, paddingLeft: 30 }}
            />
          </div>
        </ChampFiltre>
      </div>

      {/* Liste */}
      {chargement && <Etat>Chargement…</Etat>}
      {erreur && !chargement && <Etat danger><AlertCircle size={18} /> {erreur}</Etat>}
      {!chargement && !erreur && tickets.length === 0 && (
        <Etat><Inbox size={28} color={tokens.inkMuted} /> Aucun ticket avec ces filtres</Etat>
      )}

      {!chargement && tickets.length > 0 && (
        <div style={{ display:'flex', flexDirection:'column', gap: 8 }}>
          <div style={{ fontSize: 11, color: tokens.inkMuted, padding: '0 4px' }}>
            {tickets.length} ticket{tickets.length > 1 ? 's' : ''}
          </div>
          {tickets.map(t => (
            <LigneTicket key={t.id} ticket={t} onClic={() => onOuvrirTicket(t.token_public)} />
          ))}
        </div>
      )}
    </div>
  );
}

function LigneTicket({ ticket, onClic }) {
  const statut  = couleursStatut[ticket.statut] || couleursStatut.nouveau;
  const gravite = ticket.gravite_ia ? couleursGravite[ticket.gravite_ia] : null;
  const impact  = couleursImpact[ticket.impact];
  const urgent  = ticket.urgence === 'urgent';
  const sentry  = ticket.source === 'sentry';

  return (
    <div
      onClick={onClic}
      style={{
        background: urgent ? tokens.dangerBg : tokens.surface,
        border: `1px solid ${urgent ? tokens.danger + '40' : tokens.border}`,
        borderRadius: 8,
        padding: '12px 14px',
        cursor:'pointer',
        transition: 'border-color 0.15s',
      }}
      onMouseEnter={(e) => e.currentTarget.style.borderColor = tokens.accentSoft}
      onMouseLeave={(e) => e.currentTarget.style.borderColor = urgent ? tokens.danger + '40' : tokens.border}
    >
      <div style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap', marginBottom: 4 }}>
        <span style={{ fontFamily:'monospace', fontSize: 11, color: tokens.inkMuted }}>
          #{ticket.token_public}
        </span>
        <Badge bg={statut.bg} fg={statut.fg} taille="petit">{statut.label}</Badge>
        {ticket.type && couleursType[ticket.type] && (
          <Badge bg={couleursType[ticket.type].bg} fg={couleursType[ticket.type].fg} taille="petit">
            {couleursType[ticket.type].label}
          </Badge>
        )}
        {sentry && (
          <Badge bg={tokens.warningBg} fg={tokens.warning} taille="petit">
            📊 SENTRY{ticket.sentry_occurrences ? ` · ${ticket.sentry_occurrences}×` : ''}
          </Badge>
        )}
        {urgent && <Badge bg={tokens.dangerBg} fg={tokens.danger} taille="petit">URGENT</Badge>}
        {impact && <Badge bg={impact.bg} fg={impact.fg} taille="petit">{ticket.impact}</Badge>}
        {gravite && <Badge bg={gravite.bg} fg={gravite.fg} taille="petit">IA: {ticket.gravite_ia}</Badge>}
        {ticket.envoi_force && (
          <Badge bg={tokens.dangerBg} fg={tokens.danger} taille="petit">FORCÉ</Badge>
        )}
        <span style={{ fontSize: 11, color: tokens.inkMuted }}>
          {ticket.parent_service_nom ? `${ticket.parent_service_nom} / ${ticket.service_nom}` : ticket.service_nom}
        </span>
        <span style={{ marginLeft:'auto', fontSize: 11, color: tokens.inkMuted }}>
          {sentry ? 'Sentry' : ticket.declarant_nom} · {formaterDateRelative(ticket.cree_le)}
        </span>
      </div>
      <div style={{
        fontSize: 13, color: tokens.ink, lineHeight: 1.4,
        overflow:'hidden', textOverflow:'ellipsis',
        display:'-webkit-box', WebkitLineClamp: 1, WebkitBoxOrient:'vertical',
      }}>
        {ticket.resume_ia || '(en attente d\'analyse)'}
      </div>
    </div>
  );
}

function ChampFiltre({ label, style, children }) {
  return (
    <div style={style}>
      <label style={{
        display:'block', fontSize: 10, color: tokens.inkMuted,
        textTransform:'uppercase', letterSpacing: 0.5, marginBottom: 4,
        fontWeight: 600,
      }}>{label}</label>
      {children}
    </div>
  );
}

function Etat({ children, danger }) {
  return (
    <div style={{
      background: tokens.surface,
      border: `1px solid ${tokens.border}`,
      borderRadius: 10,
      padding: 32,
      textAlign:'center',
      color: danger ? tokens.danger : tokens.inkSoft,
      fontSize: 14,
      display:'flex', alignItems:'center', justifyContent:'center', gap: 10,
    }}>{children}</div>
  );
}

const selectStyle = {
  width: '100%',
  padding: '7px 10px',
  border: `1px solid ${tokens.borderStrong}`,
  borderRadius: 6,
  fontSize: 12,
  background: tokens.surface,
  outline: 'none',
};
